import type { ActionOccurrenceDto } from "@/dtos/ActionOccurrenceDto";
import type { CreateOccurrenceCommentDto } from "@/dtos/CreateOccurrenceCommentDto";
import type { DeleteOccurrenceCommentDto } from "@/dtos/DeleteOccurrenceCommentDto";
import type { ReopenOccurrenceDto } from "@/dtos/ReopenOccurrenceDto";
import type { UnificatedScreeningDto } from "@/dtos/UnificatedScreeningDto";
import type { UpdateOccurrenceCommentDto } from "@/dtos/UpdateOccurrenceCommentDto";
import type {
  TriageQueueFilters,
  TriageQueueResponse,
  OccurrenceQueueItem,
} from "@/dtos/TriageQueueDto";
import api from "@/services/api";
import { useMutation, useQuery, useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";

interface UseGetOccurrencesParams {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
  categoryId?: string;
  subcategoryId?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  cpf?: string;
  userName?: string;
  onlyReopened?: boolean;
  search?: string;
  isAnonymous?: boolean;
}

export const useGetOcurrences = (params: UseGetOccurrencesParams = {}) => {
  const {
    page = 1,
    limit = 10,
    status,
    priority,
    categoryId,
    subcategoryId,
    startDate,
    endDate,
    sortBy,
    sortOrder,
    cpf,
    userName,
    onlyReopened,
    search,
    isAnonymous,
  } = params;

  return useQuery({
    queryKey: ["getOccurrences", params],
    queryFn: async () => {
      const queryParams: Record<string, any> = {
        page,
        limit,
      };

      if (status && status !== "all") queryParams.status = status;
      if (priority && priority !== "all") queryParams.priority = priority;
      if (categoryId && categoryId !== "all") queryParams.categoryId = categoryId;
      if (subcategoryId && subcategoryId !== "all") queryParams.subcategoryId = subcategoryId;
      if (startDate) queryParams.startDate = startDate;
      if (endDate) queryParams.endDate = endDate;
      if (sortBy) queryParams.sortBy = sortBy;
      if (sortOrder) queryParams.sortOrder = sortOrder;
      if (cpf) queryParams.cpf = cpf;
      if (userName) queryParams.userName = userName;
      if (onlyReopened !== undefined) queryParams.onlyReopened = onlyReopened;
      if (search) queryParams.search = search;
      if (isAnonymous !== undefined) queryParams.isAnonymous = isAnonymous;

      const response = await api.get("/occurrences/admin/all", {
        params: queryParams,
      });

      return response.data;
    },
  });
};

export const useGetCategories = () => {
  return useQuery({
    queryKey: ["getCategories"],
    queryFn: async () => {
      const response = await api.get("/categories");
      return response.data;
    },
  });
};

export const useGetSubCategoriesByCategoryId = (categoryId: string) => {
  return useQuery({
    queryKey: ["getSubCategories", categoryId],
    queryFn: async () => {
      const response = await api.get(`/categories/${categoryId}/subcategories`);
      return response.data;
    },
    enabled: !!categoryId && categoryId !== "all",
  });
};

export const useGetOcurrenceByProtocol = (protocol: string) => {
  return useQuery({
    queryKey: ["getOccurrenceByProtocol", protocol],
    queryFn: async () => {
      const response = await api.get(`/occurrences/${protocol}`);
      return response.data;
    },
    enabled: !!protocol,
  });
};

export const useGetOccurrenceHistory = (occurrenceId: string) => {
  return useQuery({
    queryKey: ["getOccurrenceHistory", occurrenceId],
    queryFn: async () => {
      const response = await api.get(`occurrences/${occurrenceId}/history`);

      return response.data;
    },
    enabled: !!occurrenceId,
  });
};

export const useAcceptOccurrence = () => {
  return useMutation({
    mutationFn: async (occurenceId: string) => {
      const response = await api.patch(
        `/occurrences/admin/${occurenceId}/accept`,
      );

      return response.data;
    },
  });
};

export const useReturnOccurrence = () => {
  return useMutation({
    mutationKey: ["returnOccurrence"],
    mutationFn: async (actionOccurrenceDto: ActionOccurrenceDto) => {
      const response = await api.patch(
        `/occurrences/admin/${actionOccurrenceDto.occurrenceId}/return`,
        {
          reason: actionOccurrenceDto.reason,
          requiredInformation: actionOccurrenceDto.requiredInformation,
          messageToUser: actionOccurrenceDto.messageToUser,
        },
      );

      return response.data;
    },
  });
};

export const useArchiveOccurrence = () => {
  return useMutation({
    mutationKey: ["archiveOccurrence"],
    mutationFn: async (actionOccurrenceDto: ActionOccurrenceDto) => {
      const response = await api.patch(
        `/occurrences/admin/${actionOccurrenceDto.occurrenceId}/file`,
        {
          reason: actionOccurrenceDto.reason,
          justification: actionOccurrenceDto.requiredInformation,
          messageToUser: actionOccurrenceDto.messageToUser,
          ...(actionOccurrenceDto.originalProtocol &&
            actionOccurrenceDto.reason === "DUPLICATE" && {
              originalProtocol: actionOccurrenceDto.originalProtocol,
            }),
        },
      );

      return response.data;
    },
  });
};

export const useUpdateOccurrence = () => {
  return useMutation({
    mutationKey: ["updateOccurrence"],
    mutationFn: async ({
      protocol,
      data,
    }: {
      protocol: string;
      data: {
        categoryId: string;
        subcategoryId: string;
        description: string;
        sectorId: string;
        priority: string;
        originalDescription?: string;
        [key: string]: any;
      };
    }) => {
      const response = await api.patch(`/occurrences/${protocol}`, data);

      return response.data;
    },
  });
};

export const useGetSectors = () => {
  return useQuery({
    queryKey: ["getSectors"],
    queryFn: async () => {
      const response = await api.get("/sectors");
      return response.data;
    },
  });
};

export const useScreenOccurrence = () => {
  return useMutation({
    mutationKey: ["screenOccurrence"],
    mutationFn: async ({
      occurrenceId,
      action,
      categoryId,
      subcategoryId,
      priority,
      sectorId,
      notes,
    }: {
      occurrenceId: string;
      action: "approve" | "return" | "archive";
      categoryId: string;
      subcategoryId: string;
      priority: string;
      sectorId?: string;
      notes: string;
    }) => {
      const response = await api.post(
        `/occurrences/admin/${occurrenceId}/screen`,
        {
          action,
          categoryId,
          subcategoryId,
          priority,
          ...(sectorId && { sectorId }),
          notes,
        },
      );

      return response.data;
    },
  });
};

export const useGetOccurrenceComments = (occurenceId: string) => {
  return useQuery({
    queryKey: ["getOccurrenceComments", occurenceId],
    queryFn: async () => {
      const response = await api.get(`/occurrences/${occurenceId}/comments`);
      return response.data;
    },
    enabled: !!occurenceId,
  });
};

export const useCreateOccurrenceComment = () => {
  return useMutation({
    mutationKey: ["createOccurrenceComment"],
    mutationFn: async (
      createOccurrenceCommentDto: CreateOccurrenceCommentDto,
    ) => {
      const response = await api.post(
        `/occurrences/${createOccurrenceCommentDto.occurrenceId}/comments`,
        {
          type: createOccurrenceCommentDto.type,
          content: createOccurrenceCommentDto.content,
          attachments: createOccurrenceCommentDto.attachments,
        },
      );
      return response.data;
    },
  });
};

export const useUpdateOccurrenceComment = () => {
  return useMutation({
    mutationKey: ["updateOccurrenceComment"],
    mutationFn: async (
      updateOccurrenceCommentDto: UpdateOccurrenceCommentDto,
    ) => {
      const response = await api.patch(
        `/occurrences/${updateOccurrenceCommentDto.occurrenceId}/comments/${updateOccurrenceCommentDto.occurrenceCommentId}`,
        {
          content: updateOccurrenceCommentDto.content,
        },
      );
      return response.data;
    },
  });
};

export const useDeleteOccurrenceComment = () => {
  return useMutation({
    mutationKey: ["deleteOccurrenceComment"],
    mutationFn: async (
      deleteOccurrenceCommentDto: DeleteOccurrenceCommentDto,
    ) => {
      const response = await api.delete(
        `/occurrences/${deleteOccurrenceCommentDto.occurrenceId}/comments/${deleteOccurrenceCommentDto.occurrenceCommentId}`,
      );
      return response.data;
    },
  });
};

export const useUnificateScreening = () => {
  return useMutation({
    mutationKey: ["unificateScreening"],
    mutationFn: async (unificateScreening: UnificatedScreeningDto) => {
      const response = await api.patch(
        `/occurrences/admin/${unificateScreening.id}/screen`,
        {
          status: unificateScreening.status,
          observation: unificateScreening.observation,
          reason: unificateScreening.reason,
          reasonDescription: unificateScreening.reasonDescription,
          personalizedMessage: unificateScreening.personalizedMessage,
          originalProtocol: unificateScreening.originalProtocol,
          slaValue: unificateScreening.slaValue,
          slaUnit: unificateScreening.slaUnit,
        },
      );

      return response.data;
    },
  });
};

export const useCanReopenOccurrence = (id: string) => {
  return useQuery({
    queryKey: ["canReopenOccurrence", id],
    queryFn: async () => {
      const response = await api.get(`/occurrences/${id}/can-reopen`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useReopenOccurrence = () => {
  return useMutation({
    mutationKey: ["reopenOccurrence"],
    mutationFn: async (reopenOccurrenceDto: ReopenOccurrenceDto) => {
      const response = await api.post(
        `/occurrences/${reopenOccurrenceDto.id}/reopen`,
        {
          reason: reopenOccurrenceDto.reason,
          description: reopenOccurrenceDto.description,
        },
      );
      return response.data;
    },
  });
};

// ============================================
// TRIAGE QUEUE - Fila de Triagem
// ============================================

/**
 * Hook para buscar a fila de triagem com filtros
 * Backend endpoint: GET /api/occurrences/queue
 */
export const useTriageQueue = (filters: TriageQueueFilters = {}) => {
  return useInfiniteQuery<TriageQueueResponse>({
    queryKey: ["triageQueue", filters],
    queryFn: async ({ pageParam = 1 }) => {
      console.log(
        "[useTriageQueue] Executando query com filters:",
        filters,
        "pageParam:",
        pageParam,
      );

      const params = {
        page: pageParam,
        limit: filters.limit || 50,
        sortBy: filters.sortBy || "createdAt",
        sortOrder: filters.sortOrder || "asc",
        status: "AWAITING_ANALYSIS",
        ...(filters.search && { search: filters.search }),
        ...(filters.category && { categoryId: filters.category }),
        ...(filters.subcategory && { subcategoryId: filters.subcategory }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.hasAttachments !== undefined && { hasMedia: filters.hasAttachments }),
      };

      const { data } = await api.get("/occurrences/admin/all", { params });

      console.log("[useTriageQueue] Resposta da API:", data);

      // Transformar resposta da API para o formato esperado pela tela de triagem
      const now = new Date();
      const transformedData: OccurrenceQueueItem[] = data.data.map(
        (occurrence: any) => {
          const createdAt = new Date(occurrence.createdAt);
          const timeInQueueMinutes = Math.floor(
            (now.getTime() - createdAt.getTime()) / (1000 * 60),
          );

          return {
            id: occurrence.id,
            protocol: occurrence.protocol,
            createdAt: occurrence.createdAt,
            updatedAt: occurrence.updatedAt,
            category: {
              id: occurrence.category.id,
              name: occurrence.category.name,
            },
            subcategory: {
              id: occurrence.subcategory.id,
              name: occurrence.subcategory.name,
            },
            neighborhood: (() => {
              // Extrair bairro do endereço (ex: "R. Joaquim Pereira, 30 - Frecheirinha, CE, 62340-000, Brasil")
              const parts = occurrence.address.split(",");
              if (parts.length >= 3) {
                // Pegar a parte que geralmente contém o bairro (antes do estado)
                const neighborhoodPart = parts[parts.length - 3]?.trim();
                return (
                  neighborhoodPart?.split("-")[1]?.trim() ||
                  neighborhoodPart ||
                  "N/A"
                );
              }
              return "N/A";
            })(),
            address: occurrence.address,
            latitude: occurrence.latitude ? parseFloat(String(occurrence.latitude)) : undefined,
            longitude: occurrence.longitude ? parseFloat(String(occurrence.longitude)) : undefined,
            citizen: {
              id: occurrence.user?.id || occurrence.userId,
              fullName:
                occurrence.user?.profile?.fullName ||
                "Cidadão não identificado",
              cpf: occurrence.user?.profile?.cpf || undefined,
            },
            timeInQueue: timeInQueueMinutes,
            hasAttachments: occurrence.media && occurrence.media.length > 0,
            attachmentCount: occurrence.media?.length || 0,
            status: occurrence.status,
            isBeingAnalyzed: occurrence.isBeingAnalyzed || false,
            analyzedBy: occurrence.analyzedBy || undefined,
            analyzer: occurrence.analyzer || undefined,
            isReopened: occurrence.reopenCount > 0,
            description: occurrence.description,
            userPerceivedUrgency: occurrence.userPerceivedUrgency,
            priority: occurrence.priority || occurrence.userPerceivedUrgency,
            isAnonymous: occurrence.isAnonymous || false,
          };
        },
      );

      return {
        data: transformedData,
        total: data.meta.total,
        page: data.meta.page,
        limit: data.meta.limit,
        totalPages: data.meta.totalPages,
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.totalPages
        ? lastPage.page + 1
        : undefined;
    },
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    refetchInterval: 30000, // Atualização automática a cada 30 segundos (RN-010)
  });
};

/**
 * Hook para buscar ocorrências por busca rápida (protocolo, nome, CPF)
 * Backend endpoint: GET /api/occurrences/search?q=...
 */
export const useSearchOccurrences = (searchTerm: string) => {
  return useQuery<OccurrenceQueueItem[]>({
    queryKey: ["searchOccurrences", searchTerm],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 3) return [];

      // TODO: Quando backend estiver pronto
      // const response = await api.get("/occurrences/search", {
      //   params: { q: searchTerm }
      // });
      // return response.data;

      // MOCK: Filtragem local
      const allData = await mockTriageQueueData({});
      return allData.data.filter(
        (item) =>
          item.protocol.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.citizen.fullName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    },
    enabled: searchTerm.length >= 3,
  });
};

/**
 * Hook para buscar ocorrências por protocolo (para vinculação/duplicatas)
 * Backend endpoint: GET /occurrences/admin/all?search=...
 */
export const useSearchOccurrencesByProtocol = (searchTerm: string) => {
  return useQuery({
    queryKey: ["searchOccurrencesByProtocol", searchTerm],
    queryFn: async () => {
      if (!searchTerm || searchTerm.length < 3) return [];
      
      const response = await api.get("/occurrences/admin/all", {
        params: {
          search: searchTerm,
          limit: 10,
          page: 1,
        },
      });
      
      return response.data.data.map((occ: any) => ({
        id: occ.id,
        protocol: occ.protocol,
        description: occ.description,
        status: occ.status,
        category: occ.category?.name || "N/A",
        createdAt: occ.createdAt,
      }));
    },
    enabled: searchTerm.length >= 3,
  });
};

/**
 * Hook para marcar que o atendente está visualizando/analisando uma ocorrência
 * Backend endpoint: POST /api/occurrences/:id/view-log
 */
export const useLogOccurrenceView = () => {
  return useMutation({
    mutationKey: ["logOccurrenceView"],
    mutationFn: async (occurrenceId: string) => {
      // TODO: Quando backend estiver pronto
      // const response = await api.post(`/occurrences/${occurrenceId}/view-log`);
      // return response.data;

      console.log(`[MOCK] Logging view for occurrence: ${occurrenceId}`);
      return { success: true };
    },
  });
};

/**
 * Hook para registrar acesso à ocorrência para auditoria
 * Backend endpoint: POST /audit/occurrence-access
 */
export const useLogOccurrenceAccess = () => {
  return useMutation({
    mutationKey: ["logOccurrenceAccess"],
    mutationFn: async (params: {
      occurrenceId: string;
      protocol: string;
      action: string;
    }) => {
      const userDataString = localStorage.getItem("duas-vidas-user");
      const userData = userDataString ? JSON.parse(userDataString) : null;

      const payload = {
        occurrenceId: params.occurrenceId,
        userId: userData?.user?.id,
        action: params.action,
        metadata: {
          protocol: params.protocol,
          userRole: userData?.user?.role,
          timestamp: new Date().toISOString(),
        },
      };

      const response = await api.post("/audit/occurrence-access", payload);
      return response.data;
    },
  });
};

/**
 * Hook para marcar/desmarcar ocorrência como em análise
 * Backend endpoint: PATCH /occurrences/:id/analysis-status
 */
export const useUpdateAnalysisStatus = () => {
  return useMutation({
    mutationKey: ["updateAnalysisStatus"],
    mutationFn: async (params: {
      occurrenceId: string;
      isBeingAnalyzed: boolean;
    }) => {
      const response = await api.patch(
        `/occurrences/${params.occurrenceId}/analysis-status`,
        { isBeingAnalyzed: params.isBeingAnalyzed },
      );
      return response.data;
    },
  });
};

/**
 * Hook para exportar a fila de ocorrências
 * Backend endpoint: GET /api/occurrences/export?format=xlsx&...filters
 */
export const useExportQueue = () => {
  return useMutation({
    mutationKey: ["exportQueue"],
    mutationFn: async ({
      format,
      filters,
    }: {
      format: "xlsx" | "csv";
      filters: TriageQueueFilters;
    }) => {
      // TODO: Quando backend estiver pronto
      // const response = await api.get("/occurrences/export", {
      //   params: { format, ...filters },
      //   responseType: "blob",
      // });
      // return response.data;

      console.log(`[MOCK] Exporting queue as ${format} with filters:`, filters);
      return mockExportData(format, filters);
    },
  });
};

// ============================================
// MOCK DATA - Remover quando backend estiver pronto
// ============================================

const mockTriageQueueData = (
  filters: TriageQueueFilters,
): TriageQueueResponse => {
  const now = new Date();

  const mockData: OccurrenceQueueItem[] = [
    {
      id: "1",
      protocol: "202501-00042",
      createdAt: new Date(now.getTime() - 2.5 * 60 * 60 * 1000).toISOString(), // 2.5 horas atrás
      updatedAt: new Date(now.getTime() - 2 * 60 * 60 * 1000).toISOString(),
      category: { id: "1", name: "Vias Públicas" },
      subcategory: { id: "1", name: "Buraco no asfalto" },
      neighborhood: "Centro",
      address: "Rua Principal, 100",
      citizen: { id: "1", fullName: "João da Silva", cpf: "123.456.789-00" },
      timeInQueue: 150, // 2h 30min
      hasAttachments: true,
      attachmentCount: 3,
      status: "AWAITING_ANALYSIS",
      isBeingAnalyzed: false,
      isReopened: false,
      description: "Buraco grande no meio da rua, dificultando o tráfego",
    },
    {
      id: "2",
      protocol: "202501-00041",
      createdAt: new Date(now.getTime() - 5 * 60 * 60 * 1000).toISOString(), // 5 horas
      updatedAt: new Date(now.getTime() - 4.5 * 60 * 60 * 1000).toISOString(),
      category: { id: "2", name: "Iluminação Pública" },
      subcategory: { id: "2", name: "Poste sem luz" },
      neighborhood: "Jardim das Flores",
      address: "Av. das Rosas, 500",
      citizen: { id: "2", fullName: "Maria Santos", cpf: "987.654.321-00" },
      timeInQueue: 300, // 5h
      hasAttachments: true,
      attachmentCount: 1,
      status: "AWAITING_ANALYSIS",
      isBeingAnalyzed: false,
      isReopened: false,
      description: "Poste de luz apagado há 3 dias",
    },
    {
      id: "3",
      protocol: "202501-00040",
      createdAt: new Date(now.getTime() - 10 * 60 * 60 * 1000).toISOString(), // 10 horas
      updatedAt: new Date(now.getTime() - 9 * 60 * 60 * 1000).toISOString(),
      category: { id: "3", name: "Limpeza Urbana" },
      subcategory: { id: "3", name: "Lixo acumulado" },
      neighborhood: "Vila Nova",
      address: "Rua dos Comerciantes, 234",
      citizen: { id: "3", fullName: "Carlos Oliveira" },
      timeInQueue: 600, // 10h
      hasAttachments: false,
      attachmentCount: 0,
      status: "AWAITING_ANALYSIS",
      isBeingAnalyzed: true,
      analyzedBy: { id: "admin1", name: "Pedro Atendente" },
      isReopened: false,
      description: "Lixo acumulado na calçada há mais de uma semana",
    },
    {
      id: "4",
      protocol: "202501-00038",
      createdAt: new Date(now.getTime() - 26 * 60 * 60 * 1000).toISOString(), // 26 horas (CRÍTICO)
      updatedAt: new Date(now.getTime() - 25 * 60 * 60 * 1000).toISOString(),
      category: { id: "4", name: "Saúde" },
      subcategory: { id: "4", name: "Foco de dengue" },
      neighborhood: "Parque Industrial",
      address: "Rua das Indústrias, 890",
      citizen: { id: "4", fullName: "Ana Paula Costa", cpf: "456.789.123-00" },
      timeInQueue: 1560, // 26h
      hasAttachments: true,
      attachmentCount: 5,
      status: "AWAITING_ANALYSIS",
      isBeingAnalyzed: false,
      isReopened: true,
      description: "Terreno baldio com água parada e possível foco de dengue",
    },
  ];

  // Aplicar filtros (simulação)
  let filtered = [...mockData];

  if (filters.category) {
    filtered = filtered.filter((item) => item.category.id === filters.category);
  }

  if (filters.neighborhood) {
    filtered = filtered.filter(
      (item) => item.neighborhood === filters.neighborhood,
    );
  }

  if (filters.hasAttachments !== undefined) {
    filtered = filtered.filter(
      (item) => item.hasAttachments === filters.hasAttachments,
    );
  }

  // Ordenação
  if (filters.sortBy) {
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (filters.sortBy) {
        case "createdAt":
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case "timeInQueue":
          comparison = a.timeInQueue - b.timeInQueue;
          break;
        case "category":
          comparison = a.category.name.localeCompare(b.category.name);
          break;
        case "neighborhood":
          comparison = a.neighborhood.localeCompare(b.neighborhood);
          break;
      }

      return filters.sortOrder === "desc" ? -comparison : comparison;
    });
  } else {
    // Ordenação padrão: críticos primeiro, depois FIFO (RN-005 e RN-006)
    filtered.sort((a, b) => {
      const aCritical = a.timeInQueue > 1440; // > 24h
      const bCritical = b.timeInQueue > 1440;

      if (aCritical && !bCritical) return -1;
      if (!aCritical && bCritical) return 1;

      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }

  // Paginação
  const page = filters.page || 1;
  const limit = filters.limit || 20;
  const start = (page - 1) * limit;
  const end = start + limit;
  const paginated = filtered.slice(start, end);

  return {
    data: paginated,
    total: filtered.length,
    page,
    limit,
    totalPages: Math.ceil(filtered.length / limit),
  };
};

const mockExportData = (
  format: "xlsx" | "csv",
  filters: TriageQueueFilters,
) => {
  // Mock: Simula download de arquivo
  const data = mockTriageQueueData({ ...filters, page: 1, limit: 1000 });
  const blob = new Blob([JSON.stringify(data.data, null, 2)], {
    type: "application/json",
  });
  return blob;
};
